import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Send, Inbox, FileText, Clock, Eye, Ban, Copy, Forward, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ChainVisualization from '@/components/ChainVisualization';
import { copyToClipboard } from '@/utils/clipboard';
import { API } from '@/config/api';

export default function SharesPage({ token, user }) {
  const navigate = useNavigate();
  const [sentShares, setSentShares] = useState([]);
  const [receivedShares, setReceivedShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chainDialog, setChainDialog] = useState(false);
  const [chain, setChain] = useState([]);
  const [chainUdtId, setChainUdtId] = useState(null);
  const [reshareDialog, setReshareDialog] = useState(false);
  const [selectedShare, setSelectedShare] = useState(null);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [expiryHours, setExpiryHours] = useState(24);
  const [sharePermissions, setSharePermissions] = useState('view_only');

  useEffect(() => {
    console.log('SharesPage mounted with:', { 
      hasToken: !!token, 
      hasUser: !!user,
      userId: user?.user_id,
      tokenPreview: token?.substring(0, 30) + '...'
    });
    
    if (!token) {
      toast.error('No authentication token found. Please login again.');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    
    fetchAllShares();
  }, []);

  const fetchAllShares = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching shares with token:', token ? 'Token present' : 'No token');
      
      // Fetch sent shares
      const sentResponse = await axios.get(`${API}/share/direct/sent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Sent shares response:', sentResponse.data);
      setSentShares(sentResponse.data.shares);

      // Fetch received shares
      const receivedResponse = await axios.get(`${API}/share/direct/received`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Received shares response:', receivedResponse.data);
      console.log('Number of received shares:', receivedResponse.data.shares?.length || 0);
      console.log('Setting receivedShares state to:', receivedResponse.data.shares);
      setReceivedShares(receivedResponse.data.shares);
      
      // Verify state was set
      setTimeout(() => {
        console.log('receivedShares state after setting:', receivedShares.length);
      }, 100);
    } catch (error) {
      console.error('Error fetching shares:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      
      if (error.response?.status === 401 || error.response?.status === 404 || error.response?.data?.detail?.includes('User not found')) {
        toast.error('Session expired or invalid. Logging out...');
        localStorage.clear();
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else if (error.message === 'Network Error') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(`Failed to fetch shares: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const viewChain = async (udtId) => {
    if (!udtId) return;
    try {
      setChainUdtId(udtId);
      const response = await axios.get(`${API}/chain/${udtId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChain(response.data.chain || []);
      setChainDialog(true);
    } catch (error) {
      toast.error('Failed to fetch chain');
    }
  };
  const refreshChain = () => { if (chainUdtId) viewChain(chainUdtId); };

  const revokeShare = async (shareId) => {
    try {
      await axios.post(`${API}/share/direct/revoke/${shareId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Share revoked successfully');
      fetchAllShares();
    } catch (error) {
      toast.error('Failed to revoke share');
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/chat/users/search?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data.users || []);
    } catch (error) {
      setSearchResults([]);
    }
  };

  const openReshare = (share) => {
    setSelectedShare(share);
    setReshareDialog(true);
    setRecipientQuery('');
    setSearchResults([]);
    setSelectedRecipient(null);
    setShareMessage('');
    setExpiryHours(24);
    setSharePermissions('view_only');
  };

  const submitReshare = async () => {
    if (!selectedShare?.udt_id) return;
    if (!selectedRecipient?.user_id) {
      toast.error('Please select a recipient');
      return;
    }
    try {
      await axios.post(`${API}/share/reshare`, {
        udt_id: selectedShare.udt_id,
        recipient_id: selectedRecipient.user_id,
        message: shareMessage || `Forwarded: ${selectedShare.document?.file_name || selectedShare.udt_id}`,
        expiry_hours: expiryHours,
        permissions: sharePermissions || 'view_only'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reshared successfully');
      setReshareDialog(false);
      fetchAllShares();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reshare failed');
    }
  };

  const copyViewerLink = async (udtId, token) => {
    const link = `${window.location.origin}/viewer/${udtId}?token=${token}`;
    await copyToClipboard(link, 'Viewer link copied!');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'revoked': return 'bg-red-500';
      case 'expired': return 'bg-gray-500';
      default: return 'bg-blue-500';
    }
  };

  return (
    <div className="min-h-screen min-w-0" style={{ background: 'linear-gradient(135deg, #E8F2FF 0%, #F5F9FF 100%)' }}>
      <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8 w-full min-w-0">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 mb-4 sm:mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
            className="w-full sm:w-auto"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" style={{ color: '#0F52BA' }}>Document Shares</h1>
            <p className="text-xs sm:text-sm" style={{ color: '#666' }}>Manage documents you've shared and received</p>
          </div>
          <Button
            onClick={() => {
              console.log('Manual fetch triggered');
              console.log('Token:', token ? 'Present' : 'Missing');
              console.log('User:', user);
              fetchAllShares();
            }}
            variant="outline"
            className="w-full sm:w-auto sm:ml-auto"
            style={{ borderColor: '#10b981', color: '#10b981' }}
          >
            🔄 Refresh Data
          </Button>
        </div>

        <Tabs defaultValue="sent" className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-2 text-xs sm:text-sm">
            <TabsTrigger value="sent" className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Shared by Me ({sentShares.length})
            </TabsTrigger>
            <TabsTrigger value="received" className="flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Shared with Me ({receivedShares.length})
            </TabsTrigger>
          </TabsList>

          {/* Sent Shares Tab */}
          <TabsContent value="sent" className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : sentShares.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Send className="w-16 h-16 mx-auto mb-4" style={{ color: '#ccc' }} />
                  <p style={{ color: '#666' }}>No documents shared yet</p>
                  <p className="text-sm mt-2" style={{ color: '#999' }}>Share documents via Messages to see them here</p>
                </CardContent>
              </Card>
            ) : (
              sentShares.map((share) => (
                <Card key={share.share_id} className="border-l-4" style={{ borderLeftColor: '#10b981' }}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="w-5 h-5" style={{ color: '#0F52BA' }} />
                          {share.document?.file_name || 'Document'}
                        </CardTitle>
                        <div className="flex flex-wrap gap-x-2 gap-y-1 mt-2 text-xs sm:text-sm" style={{ color: '#666' }}>
                          <span className="break-all">UDT ID: <strong>{share.udt_id}</strong></span>
                          <span className="hidden sm:inline">•</span>
                          <span>Shared with: <strong>{share.recipient?.name}</strong></span>
                          <span className="text-xs truncate max-w-[180px] sm:max-w-none">({share.recipient?.email})</span>
                        </div>
                      </div>
                      <Badge className={getStatusColor(share.status)}>
                        {share.status.toUpperCase()}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm" style={{ color: '#666' }}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>Shared: {formatDate(share.created_at)}</span>
                        </div>
                        <div>
                          Expires: {formatDate(share.expiry)}
                        </div>
                      </div>
                      
                      {share.message && (
                        <div className="bg-blue-50 p-3 rounded text-sm">
                          <strong>Message:</strong> {share.message}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mt-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyViewerLink(share.udt_id, share.token)}
                          style={{ borderColor: '#10b981', color: '#10b981' }}
                          className="text-xs sm:text-sm"
                        >
                          <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Copy Link
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewChain(share.udt_id)}
                          style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
                          className="text-xs sm:text-sm"
                        >
                          <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          View Chain
                        </Button>
                        {share.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => revokeShare(share.share_id)}
                            style={{ borderColor: '#ef4444', color: '#ef4444' }}
                            className="text-xs sm:text-sm"
                          >
                            <Ban className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                            Revoke
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Received Shares Tab */}
          <TabsContent value="received" className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : receivedShares.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Inbox className="w-16 h-16 mx-auto mb-4" style={{ color: '#ccc' }} />
                  <p style={{ color: '#666' }}>No documents received yet</p>
                  <p className="text-sm mt-2" style={{ color: '#999' }}>Documents shared with you will appear here</p>
                </CardContent>
              </Card>
            ) : (
              receivedShares.map((share) => (
                <Card key={share.share_id} className="border-l-4" style={{ borderLeftColor: '#0F52BA' }}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="w-5 h-5" style={{ color: '#10b981' }} />
                          {share.document?.file_name || 'Document'}
                        </CardTitle>
                        <div className="flex items-center gap-4 mt-2 text-sm" style={{ color: '#666' }}>
                          <span>UDT ID: <strong>{share.udt_id}</strong></span>
                          <span>•</span>
                          <span>From: <strong>{share.owner?.name}</strong></span>
                          <span className="text-xs">({share.owner?.email})</span>
                        </div>
                      </div>
                      <Badge className={getStatusColor(share.status)}>
                        {share.status.toUpperCase()}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm" style={{ color: '#666' }}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>Received: {formatDate(share.created_at)}</span>
                        </div>
                        <div>
                          Expires: {formatDate(share.expiry)}
                        </div>
                      </div>
                      
                      {share.message && (
                        <div className="bg-green-50 p-3 rounded text-sm">
                          <strong>Message:</strong> {share.message}
                        </div>
                      )}

                      <div className="flex gap-2 mt-4">
                        {share.status === 'active' && (
                          <Button
                            size="sm"
                            onClick={() => navigate(`/viewer/${share.udt_id}?token=${share.token}`)}
                            style={{ background: '#10b981' }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Document
                          </Button>
                        )}
                        {!!share.allow_reshare && share.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReshare(share)}
                            style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
                          >
                            <Forward className="w-4 h-4 mr-1" />
                            Reshare
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyViewerLink(share.udt_id, share.token)}
                          style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>

        {/* Chain Visualization Dialog */}
        <Dialog open={chainDialog} onOpenChange={(open) => { setChainDialog(open); if (!open) setChainUdtId(null); }}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="flex-shrink-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <DialogTitle className="text-base sm:text-lg">Document Custody Chain</DialogTitle>
                <Button variant="outline" size="sm" onClick={refreshChain} disabled={!chainUdtId} className="flex-shrink-0">Refresh</Button>
              </div>
            </DialogHeader>
            <ChainVisualization chain={chain} udtId={chainUdtId} />
          </DialogContent>
        </Dialog>

        {/* Reshare Dialog */}
        <Dialog open={reshareDialog} onOpenChange={setReshareDialog}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Forward className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: '#0F52BA' }} />
                Reshare Document
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto min-w-0">
              <div className="p-3 rounded border bg-gray-50 min-w-0">
                <p className="font-semibold text-sm sm:text-base break-words">{selectedShare?.document?.file_name || 'Document'}</p>
                <p className="text-xs text-gray-600 font-mono break-all">UDT: {selectedShare?.udt_id}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Recipient (mobile / email / name / UDT User ID)</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={recipientQuery}
                    onChange={(e) => {
                      setRecipientQuery(e.target.value);
                      setSelectedRecipient(null);
                    }}
                    placeholder="Search recipient…"
                    className="flex-1 min-w-0"
                  />
                  <Button
                    variant="outline"
                    onClick={() => searchUsers(recipientQuery)}
                    title="Search"
                    className="sm:flex-shrink-0"
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
                {searchResults.length > 0 && !selectedRecipient && (
                  <div className="border rounded max-h-56 overflow-y-auto divide-y bg-white">
                    {searchResults.map((u) => (
                      <button
                        type="button"
                        key={u.user_id}
                        onClick={() => setSelectedRecipient(u)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="font-semibold">{u.name}</p>
                            <p className="text-xs text-gray-600 break-all">{u.email}</p>
                          </div>
                          {u.udt_user_id && (
                            <Badge variant="outline" className="font-mono text-xs">
                              {u.udt_user_id}
                            </Badge>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {selectedRecipient && (
                  <div className="p-3 rounded border bg-gray-50 flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{selectedRecipient.name}</p>
                      <p className="text-xs text-gray-600 break-all">{selectedRecipient.email}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setSelectedRecipient(null)}>
                      Change
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Message (optional)</Label>
                <Input value={shareMessage} onChange={(e) => setShareMessage(e.target.value)} placeholder="Add a message…" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Expiry (hours)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="168"
                    value={expiryHours}
                    onChange={(e) => setExpiryHours(parseInt(e.target.value) || 24)}
                    className="w-full min-w-0"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Permissions</Label>
                  <select
                    value={sharePermissions || 'view_only'}
                    onChange={(e) => setSharePermissions(e.target.value)}
                    className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="view_only">View Only</option>
                    <option value="download_view">Download and View</option>
                    <option value="print_download_view">Print, Download and View</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => setReshareDialog(false)} className="flex-1 sm:flex-initial">
                  Cancel
                </Button>
                <Button
                  onClick={submitReshare}
                  disabled={!selectedRecipient}
                  style={{ background: '#0F52BA' }}
                  className="flex-1 sm:flex-initial"
                >
                  <Forward className="w-4 h-4 mr-2" />
                  Reshare
                </Button>
              </div>

              <div className="text-xs text-gray-500">
                Note: reshare permission cannot be granted onward by the resharer (owner-only).
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
